import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AUDIT_ACTIONS } from '@hospasense/contracts';
import { useAuditEvents } from '../lib/queries.js';
import { useAuth } from '../lib/auth.js';
import { formatDateTime } from '../lib/format.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  PermissionDeniedState,
  Select,
  Skeleton,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui/index.js';

/** Auditor read surface for the append-only privileged audit log. */
export function AuditPage() {
  const { hasPermission } = useAuth();
  const [params, setParams] = useSearchParams();
  const allowed = hasPermission('audit.read');
  const query = useAuditEvents(params, allowed);

  const setParam = useCallback(
    (key: string, value: string): void => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'page') next.delete('page');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  if (!allowed) return <PermissionDeniedState what="the audit log" />;

  const page = Number(params.get('page') ?? '1');
  const data = query.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit log"
        subtitle="Append-only privileged activity. Reading this log is itself audited."
        breadcrumbs={
          <Breadcrumbs items={[{ label: 'Control', to: '/' }, { label: 'Audit log' }]} />
        }
      />

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-surface p-4">
        <Select
          label="Action"
          className="min-w-56"
          value={params.get('action') ?? ''}
          onChange={(e) => setParam('action', e.target.value)}
        >
          <option value="">All actions</option>
          {AUDIT_ACTIONS.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </Select>
      </div>

      {query.isLoading ? (
        <TableContainer>
          <Skeleton rows={10} />
        </TableContainer>
      ) : query.isError || !data ? (
        <ErrorState
          description="Audit events could not be loaded."
          onRetry={() => void query.refetch()}
        />
      ) : data.items.length === 0 ? (
        <TableContainer>
          <EmptyState
            title="No audit events match"
            description="Privileged reads and writes matching the filter would appear here."
          />
        </TableContainer>
      ) : (
        <>
          <TableContainer>
            <Table aria-label="Audit events">
              <THead>
                <tr>
                  <TH>Time</TH>
                  <TH>Actor</TH>
                  <TH>Action</TH>
                  <TH>Object</TH>
                  <TH>Outcome</TH>
                  <TH>Reason / context</TH>
                  <TH>Correlation</TH>
                </tr>
              </THead>
              <TBody>
                {data.items.map((e) => (
                  <TR key={e.id}>
                    <TD>
                      <span className="whitespace-nowrap text-xs tabular-nums">
                        {formatDateTime(e.occurredAt)}
                      </span>
                    </TD>
                    <TD className="max-w-36">
                      <span className="block truncate" title={e.actorDisplayName ?? 'system'}>
                        {e.actorDisplayName ?? <span className="text-text-muted">system</span>}
                      </span>
                    </TD>
                    <TD>
                      <span className="font-mono text-xs">{e.action}</span>
                    </TD>
                    <TD className="max-w-44">
                      <span className="block truncate" title={e.objectLabel ?? e.objectId ?? ''}>
                        {e.objectLabel ?? e.objectId ?? '—'}
                      </span>
                    </TD>
                    <TD>
                      <Badge
                        tone={
                          e.outcome === 'success'
                            ? 'success'
                            : e.outcome === 'denied'
                              ? 'error'
                              : 'warning'
                        }
                      >
                        {e.outcome}
                      </Badge>
                    </TD>
                    <TD className="max-w-52">
                      <span className="block truncate" title={e.reason ?? ''}>
                        {e.reason ?? '—'}
                      </span>
                    </TD>
                    <TD>
                      <span className="font-mono text-xs">{e.correlationId.slice(0, 8)}</span>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableContainer>
          <nav className="flex items-center justify-between text-sm" aria-label="Pagination">
            <p className="text-text-muted tabular-nums">
              {data.meta.totalItems} events · page {data.meta.page} of {data.meta.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setParam('page', String(page - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= data.meta.totalPages}
                onClick={() => setParam('page', String(page + 1))}
              >
                Next
              </Button>
            </div>
          </nav>
        </>
      )}
    </div>
  );
}
